/*
================================================================================
FILE HEADER — apps/studio-web/src/components/SaveTestModal.tsx
================================================================================

CHANGE LOG
----------
| #  | Date       | Who               | Why               | What Changed        |
|----|------------|-------------------|-------------------|---------------------|
| 1  | 2026-06-25 | Claude Sonnet 4.6 | Test Library      | Created save modal  |

FILES THAT IMPORT THIS FILE
-----------------------------
- apps/studio-web/src/App.tsx

EXPORTS COVERED
---------------
- SaveTestModal — modal for naming a test and picking/creating a suite before saving

IMPORTANT POINTS
----------------
1. If no suites exist, shows "Create new suite" inline without an extra step.
2. Suite creation is inline — no separate page needed.
================================================================================
*/

import { useState, useEffect } from 'react';
import type { IRStep } from '@plex/ir';
import { db, type DBSuite } from '../lib/db.js';
import { buildTestIR } from '../ir/builder.js';
import { newId } from '@plex/ir';

type Props = {
  steps: IRStep[];
  onSaved: (testId: string, testName: string) => void;
  onClose: () => void;
};

export function SaveTestModal({ steps, onSaved, onClose }: Props) {
  const [testName, setTestName]     = useState('');
  const [suites, setSuites]         = useState<DBSuite[]>([]);
  const [selectedSuite, setSelectedSuite] = useState('');
  const [newSuiteName, setNewSuiteName]   = useState('');
  const [creating, setCreating]     = useState(false);
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState('');

  useEffect(() => {
    db.suites.orderBy('createdAt').toArray().then((s) => {
      setSuites(s);
      if (s.length > 0 && s[0]) setSelectedSuite(s[0].id);
    });
  }, []);

  const handleSave = async () => {
    if (!testName.trim()) { setError('Please enter a test name.'); return; }

    setSaving(true);
    setError('');

    try {
      let suiteId = selectedSuite;

      // Create a new suite if requested
      if (suiteId === '__new__' || suites.length === 0) {
        const name = newSuiteName.trim() || 'Default Suite';
        const suite: DBSuite = { id: newId(), name, createdAt: Date.now(), updatedAt: Date.now() };
        await db.suites.add(suite);
        suiteId = suite.id;
      }

      const ir = buildTestIR(testName.trim(), steps);
      const testId = ir.meta.id;

      await db.tests.add({
        id: testId,
        suiteId,
        name: testName.trim(),
        tags: ir.meta.tags,
        irJson: JSON.stringify(ir),
        stepCount: steps.length,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      });

      onSaved(testId, testName.trim());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
      setSaving(false);
    }
  };

  const showNewSuite = suites.length === 0 || selectedSuite === '__new__';

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#18181b', border: '1px solid #3f3f46', borderRadius: 10,
          width: 440, padding: 24, display: 'flex', flexDirection: 'column', gap: 16,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#e4e4e7' }}>Save Test</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#71717a', cursor: 'pointer', fontSize: 20 }}>×</button>
        </div>

        <div style={{ fontSize: 12, color: '#71717a' }}>{steps.length} step{steps.length !== 1 ? 's' : ''} will be saved</div>

        {/* Test name */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={{ fontSize: 12, color: '#a1a1aa', fontWeight: 500 }}>Test name *</label>
          <input
            autoFocus
            value={testName}
            onChange={(e) => setTestName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void handleSave(); }}
            placeholder="e.g. Login — happy path"
            style={{
              background: '#09090b', border: '1px solid #3f3f46', borderRadius: 6,
              padding: '8px 10px', color: '#e4e4e7', fontSize: 13, outline: 'none',
            }}
          />
        </div>

        {/* Suite picker */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={{ fontSize: 12, color: '#a1a1aa', fontWeight: 500 }}>Suite / folder</label>
          {suites.length > 0 && (
            <select
              value={selectedSuite}
              onChange={(e) => setSelectedSuite(e.target.value)}
              style={{
                background: '#09090b', border: '1px solid #3f3f46', borderRadius: 6,
                padding: '8px 10px', color: '#e4e4e7', fontSize: 13, outline: 'none',
              }}
            >
              {suites.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
              <option value="__new__">+ Create new suite…</option>
            </select>
          )}
          {showNewSuite && (
            <input
              value={newSuiteName}
              onChange={(e) => setNewSuiteName(e.target.value)}
              placeholder={suites.length === 0 ? 'Suite name (e.g. Authentication)' : 'New suite name'}
              style={{
                background: '#09090b', border: '1px solid #3f3f46', borderRadius: 6,
                padding: '8px 10px', color: '#e4e4e7', fontSize: 13, outline: 'none',
              }}
            />
          )}
        </div>

        {error && <div style={{ fontSize: 12, color: '#ef4444' }}>{error}</div>}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{ background: 'none', border: '1px solid #3f3f46', borderRadius: 6, padding: '7px 14px', color: '#a1a1aa', cursor: 'pointer', fontSize: 13 }}
          >
            Cancel
          </button>
          <button
            onClick={() => { void handleSave(); }}
            disabled={saving || !testName.trim()}
            style={{
              background: '#7c3aed', border: 'none', borderRadius: 6, padding: '7px 16px',
              color: '#fff', cursor: saving ? 'wait' : 'pointer', fontSize: 13, fontWeight: 500,
              opacity: !testName.trim() ? 0.5 : 1,
            }}
          >
            {saving ? 'Saving…' : 'Save Test'}
          </button>
        </div>
      </div>
    </div>
  );
}
